import { useParams } from "react-router-dom";
import { useCallback, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { VMDetail } from "@/components/vm/VMDetail";
import { ReplicaPanel } from "@/components/vm/ReplicaPanel";
import { useVM, useStartVM, useStopVM, useRestartVM, useToggleOmer } from "@/hooks/useVMs";
import { getVMConsole, resetVMPassword } from "@/api/endpoints/vms";
import { useVMBackups } from "@/hooks/useBackups";
import { useTenants } from "@/hooks/useTenants";
import { Key, X } from "lucide-react";

export default function VMDetailPage() {
  const { id } = useParams<{ id: string }>();
  const vmId = Number(id);

  const { data: vm, isLoading } = useVM(vmId);
  const { data: backups } = useVMBackups(vmId);
  const startVM = useStartVM();
  const stopVM = useStopVM();
  const restartVM = useRestartVM();
  const toggleOmer = useToggleOmer();
  const { data: tenants } = useTenants();
  const currentTenant = tenants?.find((t) => t.id === vm?.tenant_id);

  const queryClient = useQueryClient();
  const [resetResult, setResetResult] = useState<{ password: string } | null>(null);
  const resetPasswordMut = useMutation({
    mutationFn: () => resetVMPassword(vmId),
    onSuccess: (r) => {
      setResetResult({ password: r.new_password });
      queryClient.invalidateQueries({ queryKey: ["vm", vmId] });
    },
  });

  const handleResetPassword = useCallback(() => {
    if (
      !window.confirm(
        "Reinitialiser le mot de passe de cette VM ?\n\n" +
          "La VM va redemarrer pour appliquer le changement via cloud-init. " +
          "Tu auras le nouveau mot de passe une seule fois — copie-le avant " +
          "de fermer la boite de dialogue.",
      )
    ) {
      return;
    }
    resetPasswordMut.mutate();
  }, [resetPasswordMut]);

  const actionError =
    startVM.error ||
    stopVM.error ||
    restartVM.error ||
    toggleOmer.error ||
    resetPasswordMut.error;

  const handleOpenConsole = useCallback(async () => {
    try {
      const info = await getVMConsole(vmId);
      if (!info?.ticket) return;

      const wsPath = `/api/vnc/${vmId}?ticket=${encodeURIComponent(info.ticket)}`;
      const qp = new URLSearchParams({
        path: wsPath,
        name: info.vm_name,
        pw: info.vnc_password || "",
        ci_user: info.ci_user || "",
        ci_password: info.ci_password || "",
      });
      // Navigate in same tab — avoids Safari self-signed cert issues with popups
      window.location.href = `/console?${qp.toString()}`;
    } catch (err) {
      console.error("Failed to open console:", err);
    }
  }, [vmId]);

  return (
    <div className="space-y-6">
      {resetResult && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() => setResetResult(null)}
        >
          <div
            className="w-full max-w-md rounded-lg border border-emerald-700 bg-gray-900 p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3 flex items-center justify-between">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-100">
                <Key className="h-4 w-4 text-emerald-400" />
                Nouveau mot de passe VM
              </h3>
              <button
                onClick={() => setResetResult(null)}
                className="rounded p-1 text-gray-500 hover:bg-gray-800"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="mb-3 text-xs text-gray-400">
              La VM redemarre ~30s pour appliquer via cloud-init. Copie
              maintenant — le mot de passe ne sera plus affiche en clair
              apres fermeture.
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 rounded bg-gray-950 px-3 py-2 font-mono text-sm text-gray-100">
                {resetResult.password}
              </code>
              <button
                onClick={() => navigator.clipboard.writeText(resetResult.password)}
                className="rounded border border-gray-700 bg-gray-800 px-3 py-2 text-xs text-gray-200 hover:bg-gray-700"
              >
                Copier
              </button>
            </div>
            <button
              onClick={() => setResetResult(null)}
              className="mt-4 w-full rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-500"
            >
              J'ai copie, fermer
            </button>
          </div>
        </div>
      )}

      <VMDetail
        vm={vm}
        backups={backups ?? []}
        isLoading={isLoading}
        omerCliEnabled={currentTenant?.omer_cli_enabled}
        isActionPending={startVM.isPending || stopVM.isPending || restartVM.isPending}
        onStart={(vmId) => startVM.mutate(vmId)}
        onStop={(vmId) => stopVM.mutate(vmId)}
        onRestart={(vmId) => restartVM.mutate(vmId)}
        onOpenConsole={handleOpenConsole}
        onToggleOmer={(vmId) => toggleOmer.mutate(vmId)}
        onResetPassword={handleResetPassword}
        isResettingPassword={resetPasswordMut.isPending}
      />

      {actionError && (
        <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4">
          <p className="text-sm text-red-400">
            {actionError instanceof Error ? actionError.message : "Operation failed"}
          </p>
        </div>
      )}

      {vm && <ReplicaPanel vm={vm} />}

    </div>
  );
}
